import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";

// Reset Password — landed here from the Resend email link. Reads the token
// from ?token=… and submits with a new password. On success, all sessions
// for that user are invalidated by the backend, and the user is bounced to
// /login to sign in fresh. Min password length matches signup (8 chars).
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== pw2) return setError("Passwords don't match.");
    if (!token) return setError("This link is missing a token — please request a new reset email.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-[80vh] flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#C9A85F]">→ Book Away</p>
        <h1 className="mt-2 text-3xl md:text-4xl leading-tight tracking-tight">Set a new password</h1>

        {done ? (
          <div
            data-testid="reset-password-done"
            className="mt-8 border border-[#C9A85F] bg-[#C9A85F]/5 p-6"
          >
            <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#8A7333]">→ Success</p>
            <p className="mt-2 text-sm">
              Your password has been updated. Redirecting you to sign in with your new credentials…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 grid gap-4">
            <p className="text-sm text-muted-foreground">Enter a new password for your Book Away account. Minimum 8 characters.</p>
            <div className="grid gap-1.5">
              <Label htmlFor="pw" className="mono text-[10px] uppercase tracking-[0.2em]">
                New password
              </Label>
              <PasswordInput
                id="pw"
                data-testid="reset-password-new-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={8}
                className="rounded-none"
                placeholder="••••••••"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pw2" className="mono text-[10px] uppercase tracking-[0.2em]">
                Confirm new password
              </Label>
              <PasswordInput
                id="pw2"
                data-testid="reset-password-confirm-input"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={8}
                className="rounded-none"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="reset-password-error" className="mono text-xs text-[#F87171]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="reset-password-submit-btn"
              disabled={busy}
              className="mt-2 w-full bg-foreground text-background px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#C9A85F] active:scale-[0.98] transition-colors disabled:opacity-60"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
            <Link
              to="/login"
              className="mono text-center text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              ← Back to sign in
            </Link>
          </form>
        )}
      </div>
    </section>
  );
}
